import { useMemo } from 'react';
import Decimal from 'decimal.js';
import { compact, isObject } from 'lodash-es';
import { Divider, InlineSelect, MenuItem, Tooltip, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { billingInitiationTextsPath, billingStagesTextsPath, billingTriggersTextsPath, makeInitialOneTimeInStages, serviceBillingStagesTermsOptions, serviceBillingTriggerOptions } from "../../../business-logic/billingLogic";
import { BillingStagesSelect } from "../../ServiceBuilder/BillingStagesSelect/BillingStagesSelect";
import { useIoContext } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useLocaleKeys } from "../../../i18n";
import { serviceBillingModel, serviceCostModel, serviceModel } from "../../../models";
import { OneTimeInStagesError, validateOneTimeInStages } from "../../../models/service/logic";
import { isAwaitingApproval, isFromAmendment } from "../../../models/service/selectors";
import { makeBillUpfrontState } from "../../../models/service-billing";
import { RecurrencePeriodTypes } from "../../../models/service-billing/model";
import { price } from "../../../models/service-cost";
import styles from "./BillingInitiationControl.module.scss";
import { InlineUpfrontSelect } from "./InlineUpfrontSelect/InlineUpfrontSelect";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const GetBillingInitiationControlTID = parentTestId => getTestIds({
  parentTestId,
  deprecatedTestIds: {
    BillingInitiationControlTestIds: {
      Root: "".concat(parentTestId, "-billing-initiation-control"),
      Occurrence: {
        Root: "".concat(parentTestId, "-occurrence"),
        Tooltip: "".concat(parentTestId, "-occurrence-tooltip"),
        Option: value => "".concat(parentTestId, "-occurrence-option-").concat(value)
      },
      Trigger: {
        Root: "".concat(parentTestId, "-trigger"),
        Tooltip: "".concat(parentTestId, "-trigger-tooltip"),
        Option: value => "".concat(parentTestId, "-trigger-option-").concat(value)
      },
      Terms: {
        Root: "".concat(parentTestId, "-terms"),
        Option: value => "".concat(parentTestId, "-terms-option-").concat(value)
      }
    }
  }
});
function serviceBillingOccurrenceOptions() {
  return compact([{
    value: 'onetime'
  }, {
    value: 'inStages'
  }, {
    value: RecurrencePeriodTypes.Weekly
  }, {
    value: RecurrencePeriodTypes.BiWeekly
  }, {
    value: RecurrencePeriodTypes.Monthly
  }, {
    value: RecurrencePeriodTypes.Quarterly
  }, {
    value: RecurrencePeriodTypes.Yearly
  }]).map(_ref => {
    let {
      value
    } = _ref;
    return {
      value,
      text: serviceBillingModel.extendedRecurrencePeriodText(value, false)
    };
  });
}
function getOccurrenceLabel(occurrence, t) {
  if (!occurrence) {
    return t.labels.once();
  }
  return serviceBillingModel.extendedRecurrencePeriodText(occurrence, true).toLowerCase();
}
function getTriggerLabel(option, t) {
  const autoLabel = t.labels.automatically();
  const manualLabel = t.labels.manually();
  const optionLabelMap = {
    approval: autoLabel,
    ongoing: autoLabel,
    manualVariable: manualLabel,
    manualFixed: manualLabel,
    oneTimeInStages: t.labels.inStages()
  };
  return optionLabelMap[option];
}
function renderMenuItem(_ref2) {
  let {
    value,
    text,
    subtext,
    testid,
    disabled,
    tooltipProps
  } = _ref2;
  return /*#__PURE__*/_jsxs(MenuItem, {
    value: value,
    variant: "multiline",
    "data-testid": testid,
    style: {
      maxWidth: 330,
      minWidth: 100
    },
    disabled: disabled,
    tooltipProps: tooltipProps,
    children: [/*#__PURE__*/_jsx(Typography, {
      weight: subtext ? 'normal' : 'light',
      children: text
    }), subtext && /*#__PURE__*/_jsx(Typography, {
      children: subtext
    })]
  }, value);
}
function matchOption(option, matcher) {
  if (option === 'manualFixed' || option === 'manualVariable') {
    matcher.manual();
  } else if (option === 'oneTimeInStages') {
    matcher.inStages();
  } else if (option === 'approval' || option === 'ongoing') {
    matcher.auto();
  }
}
function getOccurrenceValue(service) {
  const recurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing);
  const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
  if (oneTimeInStages) {
    return 'inStages';
  }
  if (!recurrencePeriod) {
    return 'onetime';
  }
  return recurrencePeriod;
}
function getTriggerValue(service) {
  const manualOption = () => serviceCostModel.isFixedPrice(service.cost) ? 'manualFixed' : 'manualVariable';
  return serviceBillingModel.accept(service.billing, {
    visitOneTimeManual: manualOption,
    visitOnApproval: () => 'approval',
    visitOneTimeInStages: () => 'oneTimeInStages',
    visitOngoing: () => 'ongoing',
    visitRepeatableManual: manualOption
  });
}
export default function BillingInitiationControl(props) {
  var _discount$isValue, _service$cost$discoun;
  const {
    testid,
    service,
    disabled,
    agreementData,
    isExistingService,
    onChange = () => {},
    onBillUpfrontModeChange,
    isReadOnly
  } = props;
  const testIds = useMemo(() => GetBillingInitiationControlTID(testid), [testid]);
  const {
    BillingInitiationControlTestIds
  } = testIds;
  const isDisabled = !!disabled;
  const disabledReason = isObject(disabled) ? disabled.reason : undefined;
  const featureFlags = useFeatureFlags();
  const {
    BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE,
    START_BILLING_AT_PHASE_2_INLINE
  } = featureFlags;
  const {
    getNow
  } = useIoContext();
  const {
    t: billingTriggersTexts
  } = useLocaleKeys(billingTriggersTextsPath);
  const {
    t: billingStagesTexts
  } = useLocaleKeys(billingStagesTextsPath);
  const {
    t: billingInitiationTexts
  } = useLocaleKeys(billingInitiationTextsPath);
  const now = useMemo(() => getNow(), []);
  const isFromAgreement = agreementData.isActiveAgreement();
  const occurrenceValue = getOccurrenceValue(service);
  const triggerValue = getTriggerValue(service);
  const isOngoing = serviceBillingModel.isOngoing(service.billing);
  const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
  const isYearly = serviceBillingModel.recurrencePeriod(service.billing) === RecurrencePeriodTypes.Yearly;
  const isAutomaticOptionSelected = serviceBillingModel.isAutomatic(service.billing);
  const shouldShowYearlyManualTooltip = isYearly && !featureFlags.YEARLY_AUTOMATIC_SERVICE && !serviceBillingModel.isManual(service.billing);
  const hasNonPercentageDiscountError = oneTimeInStages ? validateOneTimeInStages(service).includes(OneTimeInStagesError.NonPercentageDiscount) : false;
  const discount = serviceModel.discount(service);
  const hasValueDiscount = (_discount$isValue = discount === null || discount === void 0 ? void 0 : discount.isValue()) !== null && _discount$isValue !== void 0 ? _discount$isValue : false;
  const isFixedPrice = serviceCostModel.isFixedPrice(service.cost);
  const shouldDisableInStagesOption = (!isFixedPrice || hasValueDiscount) && !oneTimeInStages;
  const termsOptions = serviceBillingStagesTermsOptions({
    t: billingStagesTexts,
    now,
    agreementEffectiveDate: agreementData.effectiveDate,
    isFreshlyAddedToApprovedAgreement: isFromAgreement && !isExistingService
  });
  const occurrenceOptions = serviceBillingOccurrenceOptions();
  const triggerOptions = serviceBillingTriggerOptions({
    t: billingTriggersTexts,
    recurrencePeriod: serviceBillingModel.recurrencePeriod(service.billing),
    pricingType: service.cost.pricingType,
    billingTrigger: service.billing.trigger,
    isFromAgreement,
    isExistingService,
    featureFlags,
    withEffectiveDate: {
      effectiveDate: agreementData.effectiveDate,
      now
    }
  });
  function handleOccurrenceChange(event) {
    const newOccurrence = event.target.value;
    const currentRecurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing);
    const isRecurring = !!currentRecurrencePeriod;
    if (newOccurrence === 'onetime') {
      if (isRecurring || oneTimeInStages) {
        serviceBillingModel.accept(service.billing, {
          visitOneTimeManual: () => {},
          visitOnApproval: () => {},
          visitOneTimeInStages: () => {
            onChange(serviceModel.setOneTimeManual(service));
          },
          visitOngoing: () => {
            onChange(serviceModel.setOnApproval(service));
          },
          visitRepeatableManual: () => {
            onChange(serviceModel.setOneTimeManual(service));
          }
        });
      }
    } else if (newOccurrence === 'inStages') {
      if (!isFixedPrice || hasValueDiscount) {
        return;
      }
      onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
    } else {
      const newRecurrencePeriod = newOccurrence;
      serviceBillingModel.accept(service.billing, {
        visitOneTimeManual: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {})));
        },
        visitOnApproval: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOngoingBilling(newRecurrencePeriod, {
            isAwaitingApproval: isAwaitingApproval(service.status)
          })));
        },
        visitOneTimeInStages: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {})));
        },
        visitOngoing: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOngoingBilling(newRecurrencePeriod, {
            isAwaitingApproval: isAwaitingApproval(service.status),
            billingDayOfMonth: serviceBillingModel.billingDayOfMonth(service.billing),
            billingDayOfWeek: serviceBillingModel.billingDayOfWeek(service.billing),
            billingDayOfYear: serviceBillingModel.billingDayOfYear(service.billing),
            isBilledUpfront: serviceBillingModel.isBilledUpfront(service.billing),
            maxCharges: serviceBillingModel.maxCharges(service.billing),
            billUpfrontState: makeBillUpfrontState(serviceBillingModel.isSkipBillingOnAcceptance(service.billing))
          })));
        },
        visitRepeatableManual: () => {
          onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {
            maxCharges: serviceBillingModel.maxCharges(service.billing)
          })));
        }
      });
    }
  }
  function handleTriggerChange(event) {
    const option = event.target.value;
    const setInStages = () => onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
    const setOnApproval = () => onChange(serviceModel.setOnApproval(service));
    const setOneTimeManual = () => onChange(serviceModel.setOneTimeManual(service));
    const doNothing = () => {};
    serviceBillingModel.accept(service.billing, {
      visitOneTimeManual: () => matchOption(option, {
        manual: doNothing,
        auto: setOnApproval,
        inStages: setInStages
      }),
      visitOnApproval: () => matchOption(option, {
        manual: setOneTimeManual,
        auto: doNothing,
        inStages: setInStages
      }),
      visitOneTimeInStages: () => matchOption(option, {
        manual: setOneTimeManual,
        auto: setOnApproval,
        inStages: doNothing
      }),
      visitRepeatableManual: () => matchOption(option, {
        manual: doNothing,
        auto: () => onChange(serviceModel.setOngoing(service)),
        inStages: doNothing
      }),
      visitOngoing: () => matchOption(option, {
        manual: () => onChange(serviceModel.setRepeatableManual(service)),
        auto: doNothing,
        inStages: doNothing
      })
    });
  }
  function handleTermsChange(event) {
    if (!oneTimeInStages) return;
    const terms = event.target.value;
    onChange(serviceModel.setOneTimeInStages(service, oneTimeInStages.setTerms(terms)));
  }
  const renderDropdowns = () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE && /*#__PURE__*/_jsx(Tooltip, {
      placement: "top",
      "data-testid": BillingInitiationControlTestIds.Occurrence.Tooltip(),
      title: disabledReason === 'existingServiceInAgreement' ? billingTriggersTexts.tooltips.cannotModifyOccurrence() : hasNonPercentageDiscountError ? billingInitiationTexts.tooltips.mustBePercentage() : '',
      children: /*#__PURE__*/_jsx(InlineSelect, {
        skin: "primary",
        value: occurrenceValue,
        "data-testid": BillingInitiationControlTestIds.Occurrence.Root(),
        disabled: isDisabled,
        error: hasNonPercentageDiscountError,
        onChange: handleOccurrenceChange,
        menuMaxHeight: 200,
        SelectProps: {
          renderValue: value => getOccurrenceLabel(value, billingInitiationTexts)
        },
        children: occurrenceOptions.map(_ref3 => {
          let {
            value,
            text
          } = _ref3;
          const isInStagesOption = value === 'inStages';
          const isDisabled = isInStagesOption && shouldDisableInStagesOption;
          const tooltipTitle = isDisabled && hasValueDiscount && !oneTimeInStages ? billingInitiationTexts.tooltips.mustBePercentage() : isDisabled && !isFixedPrice && !oneTimeInStages ? billingInitiationTexts.tooltips.inStagesFixedPriceOnly() : undefined;
          return renderMenuItem({
            value,
            text,
            testid: BillingInitiationControlTestIds.Occurrence.Option(value),
            disabled: isDisabled,
            tooltipProps: tooltipTitle ? {
              title: tooltipTitle,
              placement: 'top'
            } : undefined
          });
        })
      })
    }), !(BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE && oneTimeInStages) && /*#__PURE__*/_jsx(Tooltip, {
      placement: "top",
      "data-testid": BillingInitiationControlTestIds.Trigger.Tooltip(),
      title: disabledReason === 'existingServiceInAgreement' ? billingTriggersTexts.tooltips.cannotModifyTrigger() : shouldShowYearlyManualTooltip ? billingTriggersTexts.tooltips.yearlyServicesManualOnly() : '',
      children: /*#__PURE__*/_jsx(InlineSelect, {
        skin: "primary",
        value: triggerValue,
        "data-testid": BillingInitiationControlTestIds.Trigger.Root(),
        disabled: isDisabled,
        error: shouldShowYearlyManualTooltip,
        onChange: handleTriggerChange,
        menuMaxHeight: 500,
        SelectProps: {
          renderValue: value => getTriggerLabel(value, billingInitiationTexts)
        },
        children: triggerOptions.map(_ref4 => {
          let {
            value,
            text,
            subtext
          } = _ref4;
          const isAutomaticOption = value === 'approval' || value === 'ongoing';
          const shouldDisableAutomaticOption = isYearly && !featureFlags.YEARLY_AUTOMATIC_SERVICE && isAutomaticOption;
          return renderMenuItem({
            value,
            text,
            subtext,
            testid: BillingInitiationControlTestIds.Trigger.Option(value),
            disabled: shouldDisableAutomaticOption,
            tooltipProps: shouldDisableAutomaticOption && !isAutomaticOptionSelected ? {
              title: billingTriggersTexts.tooltips.yearlyServicesManualOnly(),
              placement: 'top'
            } : undefined
          });
        })
      })
    })]
  });
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": BillingInitiationControlTestIds.Root(),
    children: [isReadOnly && /*#__PURE__*/_jsx(Divider, {
      margin: 8
    }), /*#__PURE__*/_jsxs(Typography, {
      children: [START_BILLING_AT_PHASE_2_INLINE ? 'Billed' : billingInitiationTexts.billingIsInitiated(), ' ', /*#__PURE__*/_jsxs("div", {
        className: styles.dropdowns,
        children: [triggerOptions.length < 2 && !BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE || !BILLING_OCCURRENCE_INLINE_SELECT_SERVICE_INSTANCE && oneTimeInStages ? /*#__PURE__*/_jsx(Typography, {
          as: "span",
          weight: "normal",
          children: getTriggerLabel(triggerValue, billingInitiationTexts)
        }) : renderDropdowns(), oneTimeInStages && /*#__PURE__*/_jsx(InlineSelect, {
          skin: "primary",
          value: oneTimeInStages.terms(),
          "data-testid": BillingInitiationControlTestIds.Terms.Root(),
          disabled: isDisabled,
          onChange: handleTermsChange,
          SelectProps: {
            renderValue: value => {
              const selected = termsOptions.find(opt => opt.value === value);
              return selected ? selected.text : '';
            }
          },
          children: termsOptions.map(_ref5 => {
            let {
              value,
              text,
              subtext
            } = _ref5;
            return renderMenuItem({
              value,
              text,
              subtext,
              testid: BillingInitiationControlTestIds.Terms.Option(value)
            });
          })
        }), START_BILLING_AT_PHASE_2_INLINE && isOngoing && onBillUpfrontModeChange && /*#__PURE__*/_jsx(InlineUpfrontSelect, {
          service: service,
          testid: testid,
          disabled: isDisabled,
          onChange: onBillUpfrontModeChange
        })]
      })]
    }), oneTimeInStages && /*#__PURE__*/_jsx(BillingStagesSelect, {
      discount: ((_service$cost$discoun = service.cost.discount) === null || _service$cost$discoun === void 0 ? void 0 : _service$cost$discoun.amount()) || new Decimal(0),
      totalRate: price(service.cost),
      effectiveDate: agreementData.effectiveDate,
      oneTimeInStagesBilling: oneTimeInStages,
      setOneTimeInStagesBilling: oneTimeInStagesBilling => {
        onChange(serviceModel.setOneTimeInStages(service, oneTimeInStagesBilling));
      },
      disabledReason: disabledReason,
      isAmendment: isFromAmendment(service)
    })]
  });
}